import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const EnterprisePurchase = ({ isRenewal = false, subscriptionData = null }) => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    seats: subscriptionData?.users?.active?.count || 10,
    name: user?.name || '',
    email: user?.email || '',
    companyName: user?.organization || '',
    region: 'US',
    phone: '',
    message: '',
    acceptedTerms: false
  });

  const createEnterpriseInquiryEmail = (formData) => {
    const renewalDetails = isRenewal ? `
      Current Plan Details:
      - Current Plan: ${subscriptionData?.subscription?.details?.planType || 'No active plan'}
      - Current Users: ${subscriptionData?.users?.active?.count || 0} active / ${subscriptionData?.subscription?.features?.maxUsers || 0} total
      - Status: ${subscriptionData?.subscription?.details?.status}
      - Expiration: ${subscriptionData?.subscription?.details?.endDate ? 
        new Date(subscriptionData.subscription.details.endDate).toLocaleDateString() : 'N/A'}
      - Billing Interval: ${subscriptionData?.subscription?.billing?.interval || 'N/A'}
    ` : '';

    return {
      to: process.env.REACT_APP_ENTERPRISE_EMAIL_TO,
      from: process.env.REACT_APP_EMAIL_FROM,
      replyTo: formData.email,
      subject: `ENTERPRISE ${isRenewal ? 'RENEWAL' : ''} INQUIRY: ${formData.companyName}`,
      text: `Enterprise ${isRenewal ? 'Renewal' : ''} Inquiry Details:
        - Name: ${formData.name}
        - Email: ${formData.email}
        - Organization: ${formData.companyName}
        - Region: ${formData.region}
        - Estimated Seats: ${formData.seats}
        - Phone: ${formData.phone}
        - Additional Information: ${formData.message}
        ${isRenewal ? renewalDetails : ''}
        
        ${isRenewal ? 'NOTE: This is a renewal inquiry from an existing customer.' : ''}
        Please follow up accordingly.`,
      html: `
        <h2>Enterprise ${isRenewal ? 'Renewal' : ''} Inquiry Details</h2>
        <p><strong>Name:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Organization:</strong> ${formData.companyName}</p>
        <p><strong>Region:</strong> ${formData.region}</p>
        <p><strong>Estimated Seats:</strong> ${formData.seats}</p>
        <p><strong>Phone:</strong> ${formData.phone}</p>
        <p><strong>Additional Information:</strong> ${formData.message}</p>
        ${isRenewal ? `
        <br>
        <h3>Current Plan Details</h3>
        <p><strong>Current Plan:</strong> ${subscriptionData?.subscription?.details?.planType || 'No active plan'}</p>
        <p><strong>Current Users:</strong> ${subscriptionData?.users?.active?.count || 0} active / ${subscriptionData?.subscription?.features?.maxUsers || 0} total</p>
        <p><strong>Status:</strong> ${subscriptionData?.subscription?.details?.status}</p>
        <p><strong>Expiration:</strong> ${subscriptionData?.subscription?.details?.endDate ? 
          new Date(subscriptionData.subscription.details.endDate).toLocaleDateString() : 'N/A'}</p>
        <p><strong>Billing Interval:</strong> ${subscriptionData?.subscription?.billing?.interval || 'N/A'}</p>
        ` : ''}
        ${isRenewal ? '<br><p><em><strong>NOTE:</strong> This is a renewal inquiry from an existing customer.</em></p>' : ''}
        <p>Please follow up accordingly.</p>
      `
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!formData.name || !formData.email) {
        throw new Error('Please fill in all required fields');
      }

      const emailData = createEnterpriseInquiryEmail(formData);
      const response = await axios.post(
        `${process.env.REACT_APP_API}/enterprise-inquiry`,
        emailData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        toast.success('Enterprise inquiry sent successfully');
        navigate('/login');
      }
    } catch (error) {
      console.error('Enterprise inquiry submission error:', error);
      toast.error(error.message || 'Failed to send inquiry. Please try again.');
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-sm">
      <div className="text-center mb-8">
        {(!user?.email || !user?.organization) && (
          <img src="/logo.png" alt="Lovenirai" className="h-8 mx-auto mb-6" />
        )}
        <h2 className="text-2xl font-bold text-gray-900">Enterprise Inquiry</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Estimated Number of Seats:</label>
          <input
            type="number"
            min="10"
            value={formData.seats}
            onChange={(e) => setFormData({...formData, seats: e.target.value})}
            className="mt-1 block w-full px-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Name:</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="mt-1 block w-full px-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Email:</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="mt-1 block w-full px-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Company Name:</label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => setFormData({...formData, companyName: e.target.value})}
            className="mt-1 block w-full px-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Phone Number:</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="mt-1 block w-full px-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Main Region:</label>
          <select
            value={formData.region}
            onChange={(e) => setFormData({...formData, region: e.target.value})}
            className="mt-1 block w-full px-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="US">US</option>
            {/* Add other regions as needed */}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Additional Information:</label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            rows={4}
            className="mt-1 block w-full px-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Tell us about your needs..."
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.acceptedTerms}
            onChange={(e) => setFormData({...formData, acceptedTerms: e.target.checked})}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-700">
            I accept the <a href="https://lavenirai.com/privacy-policy-explorer" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>, 
            <a href="https://lavenirai.com/explorer-tos-agreement" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"> Subscription Agreement</a>, and 
            <a href="https://lavenirai.com/eula-explorer" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"> EULA</a>.
          </label>
        </div>

        <button
          type="submit"
          className="w-full bg-[#10193A] text-white py-3 px-4 rounded-md hover:bg-opacity-90 transition-colors font-medium disabled:opacity-50"
          disabled={!formData.acceptedTerms}
        >
          Submit Inquiry
        </button>
      </form>
    </div>
  );
};

export default EnterprisePurchase;
